import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { WebAuthn, webAuthnAuthenticatorQueryKeys } from '#api';
import { AuthenticationError, trackError } from '#api/errors.ts';
import { isPublicKeyCredential, isWebAuthnAbort, isWebAuthnCancellation } from '#api/webauthn.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { page, Titled } from '#util/title.ts';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';

export function AddWebAuthnModal() {
  const navigate = useNavigate();
  const location = useAppLocation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  function returnToUserSettings() {
    void navigate('..', { replace: true });
  }

  const registerMutation = useMutation({
    mutationFn: async () => {
      const abortController = new AbortController();
      abortRef.current = abortController;

      const { warsToken, credentialCreation } = await WebAuthn.beginRegistration();
      const creationOptions = WebAuthn.prepareCreationOptions(credentialCreation);

      // The prompt stays open until the user acts on it, so tie it to this
      // modal's lifetime: a prompt left open after the modal is dismissed must
      // not register a passkey later.
      const credential = await navigator.credentials.create({
        ...creationOptions,
        signal: abortController.signal,
      });
      if (!isPublicKeyCredential(credential)) {
        throw new Error('No credential was returned from the browser.');
      }
      const serialized = WebAuthn.serializeCredential(credential);
      await auth.registerWebAuthn({
        warsToken,
        credentialCreationResponse: serialized,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webAuthnAuthenticatorQueryKeys.lists() });
      returnToUserSettings();
    },
    onError: (err) => {
      if (isWebAuthnAbort(err)) {
        // Aborted by unmount; there is no screen left to show an error on.
        return;
      }
      if (
        isWebAuthnCancellation(err) ||
        (err instanceof Error && err.message === 'No credential was returned from the browser.')
      ) {
        setErrorMessage('The request was cancelled or timed out. Please try again.');
      } else if (err instanceof AuthenticationError && err.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        void navigate('/auth/mfa/renew', { state: { from: location } });
      } else {
        trackError(err);
        setErrorMessage('Could not register your passkey. Please try again.');
      }
    },
  });

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <Modal isOpen={true} onClose={returnToUserSettings}>
      <Titled title={() => page('Add Passkey')}>
        <ModalHeader>Add passkey</ModalHeader>
        <ModalBody>
          <p className="p-md mb-16">
            Register a passkey or hardware security key for quick, secure access to your account.
          </p>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </ModalBody>
        <ModalFooter>
          <ConfirmButtonGroup
            variant="right"
            primaryButton={
              <Button
                onClick={() => {
                  setErrorMessage(undefined);
                  registerMutation.mutate();
                }}
                disabled={registerMutation.isPending || !isWebAuthnSupported()}
              >
                {registerMutation.isPending ? 'Waiting for device...' : 'Register passkey'}
              </Button>
            }
            secondaryButton={
              <Button variant="link" onClick={returnToUserSettings}>
                Cancel
              </Button>
            }
          />
        </ModalFooter>
      </Titled>
    </Modal>
  );
}
